import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { FileText, Globe, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDateBR } from "@/utils/dateUtils";
import { useInternalLink } from "@/hooks/use-internal-link";
import { linkedInApi } from "@/services/linkedin-api";
import { type LinkedInJob } from "@/types/linkedin";

type Filtro = "todos" | "applied" | "no_changes";

const FILTROS: { valor: Filtro; label: string; status: string }[] = [
  { valor: "todos", label: "Tudo", status: "applied,no_changes,cancelled" },
  { valor: "applied", label: "Aplicados", status: "applied" },
  { valor: "no_changes", label: "Sem mudança", status: "no_changes" },
];

/** Quando a decisão aconteceu (aplicado) ou quando o item foi resolvido. */
function dataDecisao(job: LinkedInJob): string {
  return job.applied_at ?? job.updated_at ?? job.created_at;
}

function rotuloDoDia(iso: string): string {
  const dia = formatDateBR(iso);
  const hoje = formatDateBR(new Date().toISOString());
  const ontem = formatDateBR(new Date(Date.now() - 86400000).toISOString());
  if (dia === hoje) return "Hoje";
  if (dia === ontem) return "Ontem";
  return dia;
}

function horaDe(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Histórico como feed por dia: o que foi decidido e quando. */
export function LinkedInHistorico() {
  const linkProps = useInternalLink();
  const [filtro, setFiltro] = useState<Filtro>("todos");

  const historicoQuery = useQuery({
    queryKey: ["linkedin-jobs", "historico", filtro],
    queryFn: () =>
      linkedInApi.listJobs({
        status: FILTROS.find((f) => f.valor === filtro)!.status,
        limit: 200,
      }),
  });
  const dias = useMemo(() => {
    const grupos = new Map<string, LinkedInJob[]>();
    for (const job of historicoQuery.data?.jobs ?? []) {
      const chave = formatDateBR(dataDecisao(job));
      const lista = grupos.get(chave);
      if (lista) lista.push(job);
      else grupos.set(chave, [job]);
    }
    return [...grupos.entries()];
  }, [historicoQuery.data]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {FILTROS.map((opcao) => (
          <Button
            key={opcao.valor}
            size="sm"
            variant={filtro === opcao.valor ? "secondary" : "ghost"}
            onClick={() => setFiltro(opcao.valor)}
          >
            {opcao.label}
          </Button>
        ))}
      </div>

      {historicoQuery.isPending ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : dias.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          Nada resolvido ainda.
        </div>
      ) : (
        <div className="space-y-4">
          {dias.map(([dia, itens]) => (
            <div key={dia} className="space-y-1.5">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">
                  {rotuloDoDia(dataDecisao(itens[0]))}
                </h3>
                <span className="text-xs text-muted-foreground">
                  {itens.length} {itens.length === 1 ? "item" : "itens"}
                </span>
              </div>
              <div className="divide-y rounded-lg border bg-card">
                {itens.map((job) => (
                  <div
                    key={job.id}
                    {...linkProps(`/linkedin/jobs/${job.id}/revisar`)}
                    role="link"
                    tabIndex={0}
                    className="flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-accent/40"
                  >
                    <span className="w-10 shrink-0 text-xs text-muted-foreground">
                      {horaDe(dataDecisao(job))}
                    </span>
                    {job.source === "scraping" ? (
                      <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {job.summary?.nome_completo ?? job.original_filename}
                      {job.summary?.empresa_atual && (
                        <span className="text-muted-foreground">
                          {" · "}
                          {job.summary.cargo_atual
                            ? `${job.summary.cargo_atual} em `
                            : ""}
                          {job.summary.empresa_atual}
                        </span>
                      )}
                    </span>
                    {job.status === "applied" ? (
                      <Badge
                        variant="outline"
                        className="shrink-0 border-emerald-200 bg-emerald-50 text-[10px] text-emerald-800"
                      >
                        {job.review_mode === "auto" ? "aplicado pela IA" : "aplicado"}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="shrink-0 text-[10px]">
                        {job.status === "no_changes" ? "sem mudança" : "cancelado"}
                      </Badge>
                    )}
                    {job.freelancer_id && (
                      <Button
                        asChild
                        variant="link"
                        className="h-auto shrink-0 p-0 text-xs"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Link to={`/freelancers/${job.freelancer_id}`}>
                          Ver freelancer
                        </Link>
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
